import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    Alert,
} from '@mui/material';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';

const NAVY = '#052F5D';

// Mirrors AMEND_REASON_MIN / AMEND_REASON_MAX in
// server/controllers/encounter/index.js — the server rejects anything
// outside this range, so match it here to fail before the round trip.
const REASON_MIN = 10;
const REASON_MAX = 500;

/**
 * Reopen a signed report for correction.
 *
 * The reason is mandatory and permanent, and lands in the internal record
 * (the encounter's amendment history and the audit log) — never on the
 * patient-facing PDF. The copy says both halves plainly: this is on the
 * record, and the reissued report itself will not advertise the change.
 */
export default function AmendDialog({ open, onClose, onConfirm, submitting, error }) {
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);

    const trimmed = reason.trim();
    const tooShort = trimmed.length < REASON_MIN;
    const tooLong = trimmed.length > REASON_MAX;
    const invalid = tooShort || tooLong;

    const close = () => {
        if (submitting) return;
        setReason('');
        setTouched(false);
        onClose();
    };

    const confirm = () => {
        setTouched(true);
        if (invalid) return;
        onConfirm(trimmed);
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ color: NAVY, fontWeight: 600, pb: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <HistoryEduOutlinedIcon fontSize="small" /> Amend this signed report?
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    The encounter becomes editable again so you can correct it. The report as it
                    stands is kept on the Documents tab, and your reason, name and the sections you
                    change are recorded permanently in this encounter&apos;s history and the audit
                    log. The reissued PDF itself carries no mention of the amendment.
                </Typography>

                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    rows={3}
                    label="Reason for the amendment"
                    placeholder="e.g. CBC returned after sign-off; diagnosis and treatment updated accordingly."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    onBlur={() => setTouched(true)}
                    disabled={submitting}
                    error={touched && invalid}
                    helperText={
                        touched && tooShort
                            ? `Give at least ${REASON_MIN} characters explaining the correction.`
                            : tooLong
                                ? `Too long — keep it under ${REASON_MAX} characters (${trimmed.length}).`
                                : 'Kept in the internal amendment history — not shown on the PDF.'
                    }
                />

                {error && (
                    <Alert severity="error" variant="outlined" sx={{ mt: 2, borderRadius: 1.5 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={close} disabled={submitting} sx={{ color: 'text.secondary' }}>
                    Cancel
                </Button>
                <Button
                    onClick={confirm}
                    disabled={submitting || (touched && invalid)}
                    variant="contained"
                    sx={{
                        bgcolor: NAVY,
                        '&:hover': { bgcolor: '#03244a' },
                        '&.Mui-disabled': { bgcolor: 'rgba(5,47,93,0.25)', color: '#fff' },
                    }}
                >
                    {submitting ? 'Opening…' : 'Open amendment'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
